import {Box} from '@dropbox/dig-foundations';
import {TrafficRoadBlockedSpot} from '@dropbox/dig-illustrations';
import * as Sentry from '@sentry/react';
import {logAuthRedirectStarted} from 'analytics/analyticsLogger';
import {getAnonymizedRoute} from 'analytics/routeInfo';
import {ApiError} from 'client';
import {NonIdealState} from 'components/shared/NonIdealState';
import {ROUTE_PATHS} from 'constant';
import {reportAndLogError} from 'helpers/logging';
import {t} from 'i18next';
import React, {useCallback} from 'react';
import {isRouteErrorResponse, useRouteError} from 'react-router';
import {getLoginUrl, isUserDeactivatedApiError} from 'utilities';

const ErrorPage = () => {
  const error = useRouteError();
  const redirectToLogin = useCallback(() => {
    const loginUrl = getLoginUrl();
    logAuthRedirectStarted(getAnonymizedRoute(), 'login');
    const currentPath = window.location.pathname.startsWith(ROUTE_PATHS.LOGOUT)
      ? ''
      : window.location.pathname.substring(1) + window.location.search + window.location.hash;

    window.location.href = loginUrl + currentPath;
  }, []);

  const redirectToWaitlist = useCallback(() => {
    logAuthRedirectStarted(getAnonymizedRoute(), 'waitlist');
    window.location.href = ROUTE_PATHS.WAITLIST;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApiError = useCallback(
    (apiError: ApiError) => {
      if (apiError.status === 401) {
        redirectToLogin();
      }
      // Handle other API error statuses if needed
    },
    [redirectToLogin]
  );

  React.useEffect(() => {
    if (
      typeof error === 'object' &&
      error !== null &&
      'status' in error &&
      (error.status === 401 || error.status === 404)
    ) {
      return;
    }
    Sentry.captureException(error, {
      level: 'fatal',
      tags: {source: 'error_page'},
    });
    reportAndLogError(error);
  }, [error]);

  if (isRouteErrorResponse(error)) {
    if (error.status === 401) {
      redirectToLogin();
    } else if (error.status === 404) {
      window.location.href = '/404';
    } else {
      reportAndLogError(error);
    }
    return <></>;
  }

  if (error instanceof ApiError && error.status === 401) {
    if (isUserDeactivatedApiError(error)) {
      redirectToWaitlist();
    } else {
      handleApiError(error);
    }
    return <></>;
  }

  return (
    <Sentry.ErrorBoundary>
      <Box style={{height: '100vh'}}>
        <NonIdealState
          image={<TrafficRoadBlockedSpot />}
          title={t('error_page_title')}
          description={t('we_are_on_it')}
        />
      </Box>
    </Sentry.ErrorBoundary>
  );
};

export {ErrorPage};
